import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TablePagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Devices as DevicesIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { adminApi } from '../services/api';
import {
  VendorResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorDeviceResponse
} from '../types/api';

interface VendorDetailsDialogProps {
  open: boolean;
  vendor: VendorResponse | null;
  onClose: () => void;
  onUpdate: () => void;
}

const VendorDetailsDialog: React.FC<VendorDetailsDialogProps> = ({
  open,
  vendor,
  onClose,
  onUpdate
}) => {
  const [devices, setDevices] = useState<VendorDeviceResponse[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (open && vendor) {
      loadVendorDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendor]);

  const loadVendorDevices = async () => {
    if (!vendor) return;
    
    setDevicesLoading(true);
    try {
      const response = await adminApi.getVendorDevices(vendor.vendor_id, {
        page: 1,
        page_size: 50,
        active_only: false
      });
      setDevices(response.devices);
    } catch (error) {
      console.error('Failed to load vendor devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  };

  if (!vendor) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          {vendor.vendor_name}
          <Chip
            label={vendor.is_active ? 'Active' : 'Inactive'}
            color={vendor.is_active ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%' }}>
          {/* Vendor Information */}
          <Accordion expanded={activeTab === 'info'} onChange={() => setActiveTab(activeTab === 'info' ? '' : 'info')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Vendor Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vendor ID
                  </Typography>
                  <Typography variant="body1">{vendor.vendor_id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vendor Code
                  </Typography>
                  <Typography variant="body1">{vendor.vendor_code}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">{vendor.description || 'No description'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Name
                  </Typography>
                  <Typography variant="body1">{vendor.contact_name || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Email
                  </Typography>
                  <Typography variant="body1">{vendor.contact_email || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Environment
                  </Typography>
                  <Chip label={vendor.environment} size="small" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">{vendor.created_by}</Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Vendor Statistics */}
          <Accordion expanded={activeTab === 'stats'} onChange={() => setActiveTab(activeTab === 'stats' ? '' : 'stats')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Statistics</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <DevicesIcon color="primary" />
                      <Typography variant="h6">{vendor.device_count || 0}</Typography>
                      <Typography variant="caption">Total Devices</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <DevicesIcon color="success" />
                      <Typography variant="h6">{vendor.active_device_count || 0}</Typography>
                      <Typography variant="caption">Active Devices</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center' }}>
                      <TrendingUpIcon color="info" />
                      <Typography variant="h6">{vendor.total_requests || 0}</Typography>
                      <Typography variant="caption">Total Requests</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Vendor Devices */}
          <Accordion expanded={activeTab === 'devices'} onChange={() => setActiveTab(activeTab === 'devices' ? '' : 'devices')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Devices ({devices.length})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {devicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>Device Name</TableCell>
                        <TableCell>Model</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Permissions</TableCell>
                        <TableCell>Requests</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {devices.map((device) => (
                        <TableRow key={device.id}>
                          <TableCell>{device.device_id}</TableCell>
                          <TableCell>{device.device_name}</TableCell>
                          <TableCell>{device.device_model || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={device.is_active ? 'Active' : 'Inactive'}
                              color={device.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{device.permission_count || 0}</TableCell>
                          <TableCell>{device.total_requests || 0}</TableCell>
                        </TableRow>
                      ))}
                      {devices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                            No devices found for this vendor
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onUpdate}>
          Edit Vendor
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CreateEditVendorDialogProps {
  open: boolean;
  vendor: VendorResponse | null;
  onClose: () => void;
  onSave: () => void;
}

const CreateEditVendorDialog: React.FC<CreateEditVendorDialogProps> = ({
  open,
  vendor,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    vendor_id: 0,
    vendor_name: '',
    vendor_code: '',
    description: '',
    contact_email: '',
    contact_name: '',
    environment: 'testing' as 'testing' | 'production',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (vendor) {
        // Edit mode
        setFormData({
          vendor_id: vendor.vendor_id,
          vendor_name: vendor.vendor_name,
          vendor_code: vendor.vendor_code,
          description: vendor.description || '',
          contact_email: vendor.contact_email || '',
          contact_name: vendor.contact_name || '',
          environment: vendor.environment as 'testing' | 'production',
          is_active: vendor.is_active
        });
      } else {
        // Create mode
        setFormData({
          vendor_id: 0,
          vendor_name: '',
          vendor_code: '',
          description: '',
          contact_email: '',
          contact_name: '',
          environment: 'testing',
          is_active: true
        });
      }
      setError(null);
    }
  }, [open, vendor]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      if (vendor) {
        // Update existing vendor
        const updateData: UpdateVendorRequest = {
          vendor_name: formData.vendor_name,
          description: formData.description,
          contact_email: formData.contact_email,
          contact_name: formData.contact_name,
          is_active: formData.is_active
        };
        await adminApi.updateVendor(vendor.vendor_id, updateData);
      } else {
        // Create new vendor
        const createData: CreateVendorRequest = {
          vendor_id: formData.vendor_id,
          vendor_name: formData.vendor_name,
          vendor_code: formData.vendor_code,
          description: formData.description,
          contact_email: formData.contact_email,
          contact_name: formData.contact_name,
          environment: formData.environment
        };
        await adminApi.createVendor(createData);
      }
      
      onSave();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {vendor ? 'Edit Vendor' : 'Create New Vendor'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {!vendor && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor ID"
                  type="number"
                  value={formData.vendor_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_id: parseInt(e.target.value) || 0 }))}
                  required
                  helperText="Unique 16-bit vendor identifier"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor Code"
                  value={formData.vendor_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, vendor_code: e.target.value.toUpperCase() }))}
                  required
                  helperText="Short code (e.g., NTNG, SONY)"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Vendor Name"
              value={formData.vendor_name}
              onChange={(e) => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Name"
              value={formData.contact_name}
              onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
            />
          </Grid>
          
          {!vendor && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Environment</InputLabel>
                <Select
                  value={formData.environment}
                  label="Environment"
                  onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value as 'testing' | 'production' }))}
                >
                  <MenuItem value="testing">Testing</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {vendor && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.vendor_name || (!vendor && (!formData.vendor_id || !formData.vendor_code))}
        >
          {loading ? <CircularProgress size={20} /> : (vendor ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Vendors: React.FC = () => {
  const [vendors, setVendors] = useState<VendorResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState<VendorResponse | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<VendorResponse | null>(null);

  useEffect(() => {
    loadVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, activeOnly]);

  const loadVendors = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminApi.getVendors({
        page: page + 1, // API uses 1-based pagination
        page_size: rowsPerPage,
        active_only: activeOnly
      });
      
      setVendors(response.vendors);
      setTotalCount(response.total_count);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (vendor: VendorResponse) => {
    setSelectedVendor(vendor);
    setDetailsDialogOpen(true);
  };

  const handleEditVendor = (vendor: VendorResponse) => {
    setEditingVendor(vendor);
    setCreateEditDialogOpen(true);
  };

  const handleCreateVendor = () => {
    setEditingVendor(null);
    setCreateEditDialogOpen(true);
  };

  const handleVendorSaved = () => {
    loadVendors();
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.vendor_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && vendors.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Vendor Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateVendor}
        >
          Create Vendor
        </Button>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={activeOnly}
                    onChange={(e) => setActiveOnly(e.target.checked)}
                  />
                }
                label="Active Only"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={loadVendors} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
                <Tooltip title="Filter options">
                  <IconButton>
                    <FilterIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Vendors Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Devices</TableCell>
                  <TableCell>Requests</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && vendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVendors.map((vendor) => (
                    <TableRow key={vendor.id} hover>
                      <TableCell>{vendor.vendor_id}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{vendor.vendor_name}</Typography>
                        {vendor.description && (
                          <Typography variant="caption" color="text.secondary">
                            {vendor.description.substring(0, 50)}
                            {vendor.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={vendor.vendor_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={vendor.environment} 
                          size="small"
                          color={vendor.environment === 'production' ? 'error' : 'info'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DevicesIcon fontSize="small" />
                          {vendor.device_count || 0}
                          {vendor.active_device_count !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              ({vendor.active_device_count} active)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{vendor.total_requests?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.is_active ? 'Active' : 'Inactive'}
                          color={vendor.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(vendor)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Vendor">
                            <IconButton size="small" onClick={() => handleEditVendor(vendor)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!loading && filteredVendors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'No vendors found matching your search.' : 'No vendors found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <VendorDetailsDialog
        open={detailsDialogOpen}
        vendor={selectedVendor}
        onClose={() => setDetailsDialogOpen(false)}
        onUpdate={() => {
          setDetailsDialogOpen(false);
          if (selectedVendor) {
            handleEditVendor(selectedVendor);
          }
        }}
      />

      <CreateEditVendorDialog
        open={createEditDialogOpen}
        vendor={editingVendor}
        onClose={() => setCreateEditDialogOpen(false)}
        onSave={handleVendorSaved}
      />
    </Box>
  );
};

export default Vendors; 